import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import {
  DataTable,
  Drawer,
  EmptyState,
  ErrorState,
  LoadingSkeleton,
  MetricCard,
  PageHeader,
  StatusBadge,
} from '../components/ui';
import {
  Business,
  CompetitorComparisonDto,
  CompetitorDto,
  CompetitorStatus,
  CreateCompetitorRequest,
  UpdateCompetitorRequest,
  businessApi,
  competitorsApi,
} from '../lib/api';

type FormState = {
  name: string;
  websiteUrl: string;
  googleMapsUrl: string;
  location: string;
  notes: string;
  status: CompetitorStatus;
};

const emptyForm: FormState = {
  name: '',
  websiteUrl: '',
  googleMapsUrl: '',
  location: '',
  notes: '',
  status: 'ACTIVE',
};

function formatDate(value: string | null) {
  if (!value) return 'Not analyzed';
  return new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function statusTone(status: CompetitorStatus) {
  if (status === 'ACTIVE') return 'success';
  if (status === 'PAUSED') return 'warning';
  return 'neutral';
}

export default function CompetitorsPage() {
  const { businessId, competitorId } = useParams<{ businessId?: string; competitorId?: string }>();
  const navigate = useNavigate();

  const [business, setBusiness] = useState<Business | null>(null);
  const [businessList, setBusinessList] = useState<Business[]>([]);
  const [activeBizId, setActiveBizId] = useState<number | null>(businessId ? parseInt(businessId, 10) : null);
  const [competitors, setCompetitors] = useState<CompetitorDto[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CompetitorStatus | 'ALL'>('ALL');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [analyzingId, setAnalyzingId] = useState<number | null>(null);
  const [selectedForCompare, setSelectedForCompare] = useState<CompetitorDto | null>(null);
  const [comparisonData, setComparisonData] = useState<CompetitorComparisonDto | null>(null);
  const [loadingCompare, setLoadingCompare] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState<CompetitorDto | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormState>(emptyForm);
  const [deletingCompetitor, setDeletingCompetitor] = useState<CompetitorDto | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    businessApi
      .list()
      .then((list) => {
        if (!mounted) return;
        setBusinessList(list);

        if (businessId) {
          const parsed = parseInt(businessId, 10);
          const found = list.find((item) => item.id === parsed);
          if (found) {
            setBusiness(found);
            setActiveBizId(parsed);
            localStorage.setItem('active_dashboard_business_id', String(parsed));
          } else if (list.length > 0) {
            navigate(`/businesses/${list[0].id}/competitors`, { replace: true });
          }
          return;
        }

        const stored = localStorage.getItem('active_dashboard_business_id');
        const storedId = stored ? parseInt(stored, 10) : null;
        const found = storedId ? list.find((item) => item.id === storedId) : null;
        const target = found || list[0];
        if (target) navigate(`/businesses/${target.id}/competitors`, { replace: true });
        else setLoading(false);
      })
      .catch(() => {
        setError('We could not load your businesses.');
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [businessId, navigate]);

  const fetchCompetitors = useCallback(async () => {
    if (!activeBizId) return;
    setLoading(true);
    setError(null);
    setCompetitors([]);
    setSelectedForCompare(null);
    setComparisonData(null);
    try {
      const res = await competitorsApi.list(activeBizId, {
        page,
        pageSize,
        search: search.trim() || undefined,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
      });
      setCompetitors(res.items);
      setTotalItems(res.pagination.totalItems);
      setTotalPages(res.pagination.totalPages);
    } catch (err: any) {
      setError(err?.message || 'We could not load competitors.');
    } finally {
      setLoading(false);
    }
  }, [activeBizId, page, pageSize, search, statusFilter]);

  useEffect(() => {
    void fetchCompetitors();
  }, [fetchCompetitors]);

  useEffect(() => {
    if (competitorId && activeBizId && competitors.length > 0) {
      const parsedCompId = parseInt(competitorId, 10);
      if (!isNaN(parsedCompId)) void openComparison(parsedCompId);
    }
  }, [competitorId, activeBizId, competitors.length]);

  const activeCount = useMemo(() => competitors.filter((item) => item.status === 'ACTIVE').length, [competitors]);
  const totalAnalyzed = useMemo(() => competitors.filter((item) => item.lastAnalyzedAt !== null).length, [competitors]);

  function handleBusinessChange(newId: number) {
    setActiveBizId(newId);
    const nextBusiness = businessList.find((item) => item.id === newId) ?? null;
    setBusiness(nextBusiness);
    localStorage.setItem('active_dashboard_business_id', String(newId));
    window.dispatchEvent(new CustomEvent('active-business-change', { detail: newId }));
    navigate(`/businesses/${newId}/competitors`);
  }

  function openAddModal() {
    setEditingCompetitor(null);
    setFormData(emptyForm);
    setFormError(null);
    setIsFormOpen(true);
  }

  function openEditModal(competitor: CompetitorDto) {
    setEditingCompetitor(competitor);
    setFormData({
      name: competitor.name,
      websiteUrl: competitor.websiteUrl,
      googleMapsUrl: competitor.googleMapsUrl || '',
      location: competitor.location || '',
      notes: competitor.notes || '',
      status: competitor.status,
    });
    setFormError(null);
    setIsFormOpen(true);
  }

  async function handleFormSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!activeBizId) return;
    if (!formData.name.trim()) {
      setFormError('Competitor name is required.');
      return;
    }
    if (!formData.websiteUrl.trim()) {
      setFormError('Website URL is required.');
      return;
    }

    setFormSubmitting(true);
    setFormError(null);
    try {
      if (editingCompetitor) {
        const updatePayload: UpdateCompetitorRequest = {
          name: formData.name.trim(),
          websiteUrl: formData.websiteUrl.trim(),
          googleMapsUrl: formData.googleMapsUrl.trim() || null,
          location: formData.location.trim() || null,
          notes: formData.notes.trim() || null,
          status: formData.status,
        };
        await competitorsApi.update(activeBizId, editingCompetitor.id, updatePayload);
      } else {
        const createPayload: CreateCompetitorRequest = {
          name: formData.name.trim(),
          websiteUrl: formData.websiteUrl.trim(),
          googleMapsUrl: formData.googleMapsUrl.trim() || null,
          location: formData.location.trim() || null,
          notes: formData.notes.trim() || null,
        };
        await competitorsApi.create(activeBizId, createPayload);
      }
      setIsFormOpen(false);
      await fetchCompetitors();
    } catch (err: any) {
      setFormError(err?.message || 'We could not save this competitor.');
    } finally {
      setFormSubmitting(false);
    }
  }

  async function handleDeleteConfirm() {
    if (!activeBizId || !deletingCompetitor) return;
    setDeleteSubmitting(true);
    try {
      await competitorsApi.delete(activeBizId, deletingCompetitor.id);
      setDeletingCompetitor(null);
      await fetchCompetitors();
    } catch (err: any) {
      setAnalysisError(err?.message || 'We could not remove that competitor.');
    } finally {
      setDeleteSubmitting(false);
    }
  }

  async function handleAnalyze(competitor: CompetitorDto) {
    if (!activeBizId) return;
    setAnalyzingId(competitor.id);
    setAnalysisError(null);
    try {
      const updated = await competitorsApi.analyze(activeBizId, competitor.id);
      setCompetitors((current) => current.map((item) => (item.id === competitor.id ? updated : item)));
      if (selectedForCompare?.id === competitor.id) await openComparison(competitor.id);
    } catch (err: any) {
      setAnalysisError(err?.message || `We could not analyze ${competitor.name}.`);
    } finally {
      setAnalyzingId(null);
    }
  }

  async function openComparison(compId: number) {
    if (!activeBizId) return;
    const target = competitors.find((item) => item.id === compId) ?? null;
    setSelectedForCompare(target);
    setLoadingCompare(true);
    setCompareError(null);
    try {
      setComparisonData(await competitorsApi.compare(activeBizId, compId));
    } catch (err: any) {
      setCompareError(err?.message || 'We could not load the competitor comparison.');
    } finally {
      setLoadingCompare(false);
    }
  }

  function closeComparison() {
    setSelectedForCompare(null);
    setComparisonData(null);
    if (competitorId) navigate(`/businesses/${activeBizId}/competitors`, { replace: true });
  }

  return (
    <DashboardLayout
      title="Competitors"
      subtitle="Compare public local SEO signals without fabricated metrics."
      breadcrumbs={[
        { label: 'Dashboard', to: '/dashboard' },
        { label: business?.name || 'Business', to: business ? `/businesses/${business.id}/dashboard` : '/businesses' },
        { label: 'Competitors' },
      ]}
    >
      <PageHeader
        eyebrow="Local Market"
        title="Competitor Intelligence"
        description="Track nearby competitors, analyze their public websites, and compare deterministic SEO signals."
        actions={
          <>
            {businessList.length > 1 && (
              <select
                className="form-input compact-select"
                value={activeBizId ?? ''}
                onChange={(event) => handleBusinessChange(parseInt(event.target.value, 10))}
                aria-label="Switch business"
              >
                {businessList.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            )}
            <button className="btn btn-primary" type="button" onClick={openAddModal}>
              Add Competitor
            </button>
          </>
        }
        meta={<StatusBadge tone="info">Public website signals only</StatusBadge>}
      />

      {error && <ErrorState message={error} onRetry={() => void fetchCompetitors()} />}
      {analysisError && <ErrorState title="Competitor action failed" message={analysisError} />}

      <div className="ui-metric-grid">
        <MetricCard label="Tracked Competitors" value={totalItems} context={`${activeCount} active`} tone="info" />
        <MetricCard label="Analyzed Websites" value={totalAnalyzed} context={`${Math.max(totalItems - totalAnalyzed, 0)} pending crawl`} tone="success" />
        <MetricCard label="GBP Comparison" value="Unavailable" context="Requires third-party public data access" tone="neutral" />
        {/* <MetricCard label="Private Rankings" value="Not shared" context="Search Console data remains owner-only" tone="neutral" /> */}
      </div>

      <section className="ui-section">
        <div className="ui-toolbar">
          <div className="ui-search-control">
            <label htmlFor="competitor-search">Search competitors</label>
            <input
              id="competitor-search"
              className="form-input"
              type="search"
              placeholder="Name, domain, or location"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="ui-filter-control">
            <label htmlFor="competitor-status">Status</label>
            <select
              id="competitor-status"
              className="form-input compact-select"
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value as CompetitorStatus | 'ALL');
                setPage(1);
              }}
            >
              <option value="ALL">All statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="PAUSED">Paused</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>
        </div>

        {loading ? (
          <LoadingSkeleton rows={8} />
        ) : competitors.length === 0 ? (
          <EmptyState
            title="No competitors tracked yet"
            description="Add the local businesses you see in search results, maps, or sales conversations."
            action={<button className="btn btn-primary" type="button" onClick={openAddModal}>Add Competitor</button>}
          />
        ) : (
          <DataTable>
            <thead>
              <tr>
                <th>Competitor</th>
                <th>Status</th>
                <th>Analysis</th>
                <th>Website Signals</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {competitors.map((competitor) => (
                <tr key={competitor.id}>
                  <td>
                    <div className="table-primary">{competitor.name}</div>
                    <div className="table-secondary">{competitor.domain}</div>
                    {competitor.location && <div className="table-secondary">{competitor.location}</div>}
                  </td>
                  <td><StatusBadge tone={statusTone(competitor.status)}>{competitor.status}</StatusBadge></td>
                  <td>
                    <div className="table-primary">{formatDate(competitor.lastAnalyzedAt)}</div>
                    {competitor.analysis?.overallScore !== undefined && competitor.analysis?.overallScore !== null && (
                      <div className="table-secondary">Score {competitor.analysis.overallScore}/100</div>
                    )}
                  </td>
                  <td>
                    <div className="signal-row">
                      <span>HTTPS</span>
                      <StatusBadge tone={competitor.analysis?.isHttps ? 'success' : 'neutral'}>
                        {competitor.analysis ? (competitor.analysis.isHttps ? 'Yes' : 'No') : 'Unknown'}
                      </StatusBadge>
                    </div>
                    <div className="table-secondary">{competitor.analysis?.technicalIssues.length ?? 0} technical issues</div>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-secondary btn-sm" type="button" onClick={() => void openComparison(competitor.id)}>
                        Compare
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        type="button"
                        disabled={analyzingId === competitor.id}
                        onClick={() => void handleAnalyze(competitor)}
                      >
                        {analyzingId === competitor.id ? 'Analyzing' : 'Analyze'}
                      </button>
                      <button className="btn btn-ghost btn-sm" type="button" onClick={() => openEditModal(competitor)}>
                        Edit
                      </button>
                      <button className="btn btn-ghost btn-sm danger-text" type="button" onClick={() => setDeletingCompetitor(competitor)}>
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}

        {totalPages > 1 && (
          <div className="ui-pagination">
            <span>Page {page} of {totalPages}</span>
            <div>
              <button className="btn btn-secondary btn-sm" type="button" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>
                Previous
              </button>
              <button className="btn btn-secondary btn-sm" type="button" disabled={page >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>
                Next
              </button>
            </div>
          </div>
        )}
      </section>

      <Drawer
        open={Boolean(selectedForCompare)}
        title={selectedForCompare ? `Compare ${selectedForCompare.name}` : 'Competitor comparison'}
        subtitle="Public website signals and deterministic opportunities."
        onClose={closeComparison}
      >
        {loadingCompare ? (
          <LoadingSkeleton rows={6} />
        ) : compareError ? (
          <ErrorState message={compareError} />
        ) : comparisonData ? (
          <div className="comparison-drawer-content">
            <div className="comparison-grid">
              <div className="comparison-column">
                <h3>{comparisonData.business.name}</h3>
                <p>{comparisonData.business.domain || comparisonData.business.websiteUrl || 'No website configured'}</p>
                <MetricCard label="Audit Score" value={comparisonData.business.overallScore ?? 'Missing'} context="Your latest audit" tone="info" />
              </div>
              <div className="comparison-column">
                <h3>{comparisonData.competitor.name}</h3>
                <p>{comparisonData.competitor.domain}</p>
                <MetricCard label="Audit Score" value={comparisonData.competitor.overallScore ?? 'Not analyzed'} context="Public crawl result" tone="neutral" />
              </div>
            </div>

            <DataTable>
              <thead>
                <tr>
                  <th>Signal</th>
                  <th>Your Business</th>
                  <th>Competitor</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>HTTPS</td>
                  <td>{comparisonData.business.isHttps === null ? 'Unknown' : comparisonData.business.isHttps ? 'Yes' : 'No'}</td>
                  <td>{comparisonData.competitor.isHttps === null ? 'Unknown' : comparisonData.competitor.isHttps ? 'Yes' : 'No'}</td>
                </tr>
                <tr>
                  <td>Page title</td>
                  <td>{comparisonData.business.pageTitle || 'Missing'}</td>
                  <td>{comparisonData.competitor.pageTitle || 'Missing'}</td>
                </tr>
                <tr>
                  <td>Meta description</td>
                  <td>{comparisonData.business.metaDescription || 'Missing'}</td>
                  <td>{comparisonData.competitor.metaDescription || 'Missing'}</td>
                </tr>
                <tr>
                  <td>Technical issues</td>
                  <td>{comparisonData.business.technicalIssuesCount ?? 'Unknown'}</td>
                  <td>{comparisonData.competitor.technicalIssuesCount ?? 'Unknown'}</td>
                </tr>
              </tbody>
            </DataTable>

            <div className="ui-callout">
              <strong>Unavailable private signals</strong>
              <p>{comparisonData.signals.gbpUnavailableReason}</p>
              <p>{comparisonData.signals.privateKeywordsUnavailableReason}</p>
            </div>

            <div className="opportunity-stack">
              <h3>Deterministic Opportunities</h3>
              {comparisonData.opportunities.length === 0 ? (
                <EmptyState title="No major competitive gap detected" description="Run analysis again after new audit or competitor data is available." />
              ) : (
                comparisonData.opportunities.map((opportunity, index) => (
                  <div className="opportunity-row" key={`${opportunity.title}-${index}`}>
                    <div>
                      <StatusBadge tone={opportunity.impact === 'HIGH' ? 'danger' : opportunity.impact === 'MEDIUM' ? 'warning' : 'info'}>
                        {opportunity.impact}
                      </StatusBadge>
                    </div>
                    <div>
                      <h4>{opportunity.title}</h4>
                      <p>{opportunity.description}</p>
                      <strong>{opportunity.recommendation}</strong>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : null}
      </Drawer>

      {isFormOpen && (
        <div className="modal-backdrop">
          <div className="modal-box competitor-modal" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h3 className="modal-title">{editingCompetitor ? 'Edit Competitor' : 'Add Competitor'}</h3>
              <button type="button" className="modal-close-btn" onClick={() => setIsFormOpen(false)}>x</button>
            </div>
            {formError && <ErrorState title="Form error" message={formError} />}
            <form onSubmit={handleFormSubmit}>
              <div className="form-group">
                <label className="form-label" htmlFor="competitor-name">Competitor Name</label>
                <input id="competitor-name" className="form-input" value={formData.name} onChange={(event) => setFormData({ ...formData, name: event.target.value })} required maxLength={200} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="competitor-url">Website URL</label>
                <input id="competitor-url" className="form-input" type="url" value={formData.websiteUrl} onChange={(event) => setFormData({ ...formData, websiteUrl: event.target.value })} required maxLength={500} />
                <span className="form-hint">Only public HTTP/HTTPS URLs are accepted by the crawler.</span>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="competitor-location">Location</label>
                  <input id="competitor-location" className="form-input" value={formData.location} onChange={(event) => setFormData({ ...formData, location: event.target.value })} maxLength={200} />
                </div>
                {editingCompetitor && (
                  <div className="form-group">
                    <label className="form-label" htmlFor="competitor-status-input">Status</label>
                    <select id="competitor-status-input" className="form-input" value={formData.status} onChange={(event) => setFormData({ ...formData, status: event.target.value as CompetitorStatus })}>
                      <option value="ACTIVE">Active</option>
                      <option value="PAUSED">Paused</option>
                      <option value="ARCHIVED">Archived</option>
                    </select>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="competitor-maps">Google Maps URL</label>
                <input id="competitor-maps" className="form-input" type="url" value={formData.googleMapsUrl} onChange={(event) => setFormData({ ...formData, googleMapsUrl: event.target.value })} maxLength={1000} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="competitor-notes">Notes</label>
                <textarea id="competitor-notes" className="form-input form-textarea" rows={3} value={formData.notes} onChange={(event) => setFormData({ ...formData, notes: event.target.value })} maxLength={2000} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setIsFormOpen(false)} disabled={formSubmitting}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={formSubmitting}>{formSubmitting ? 'Saving' : editingCompetitor ? 'Update Competitor' : 'Add Competitor'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deletingCompetitor && (
        <div className="modal-backdrop">
          <div className="modal-box" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h3 className="modal-title">Remove competitor?</h3>
              <button type="button" className="modal-close-btn" onClick={() => setDeletingCompetitor(null)}>x</button>
            </div>
            <p className="modal-copy">
              Remove <strong>{deletingCompetitor.name}</strong> from tracked competitors. This does not affect audits or business data.
            </p>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setDeletingCompetitor(null)} disabled={deleteSubmitting}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={() => void handleDeleteConfirm()} disabled={deleteSubmitting}>{deleteSubmitting ? 'Removing' : 'Remove'}</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
